import Chip, { ChipProps } from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
//import ErrorIcon from '@mui/icons-material/Error';
//import WarningIcon from '@mui/icons-material/Warning';
//import BlockIcon from '@mui/icons-material/Block';
//import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { Detaljer, Success, Merknad, Info, Error, Archive, Workflow, Warning } from '@byggforsk/fundament-icons';
// dbo.VersionStatus (ie.Figur Status siden de følger anvisning)
// StatusId  StatusText  StatusCode
// 0 Ubestemt  U
// 1 Publisert P // aktiv
// 2 Under revisjon  R
// 3 Midlertidig tilbaketrukket  M
// 4 Arkivert  A

export type StatusCode = 0 | 1 | 2 | 3 | 4;

type StatusChipProps = Omit<ChipProps, 'icon' | 'label'> & {
  status: StatusCode;
  lastUpdated?: string;
  published?: string;
  description?: string;
  showLabel?: boolean;
  onRemove?: () => void;
  onReplace?: () => void;
  onAdd?: () => void;
};

// Remove the unused status 1 entry and restrict the config to only valid palette keys.
// Dbl. check that this displays correctly in the UI. Refreshing seems to change status sometimes. Might be correct, but needs to be verified.
const statusConfig: Record<StatusCode, { icon: React.ElementType; label: string; color: 'info' | 'error' | 'secondary' | 'warning' }> = {
  0: {
    // 0	Ubestemt
    icon: Error,
    label: 'Merknad',
    color: 'error',
  },
  1: {
    // 1	Publisert
    icon: Success,
    label: 'Publisert',
    color: 'info',
  },
  2: {
    // 2	Under revisjon
    icon: Workflow,
    label: 'Ikke siste versjon',
    color: 'warning',
  },
  3: {
    // 3	Midlertidig tilbaketrukket
    icon: Warning,
    label: 'Tilbaketrukket',
    color: 'warning',
  },
  4: {
    // 4	Arkivert
    icon: Archive,
    label: 'Erstattet',
    color: 'warning',
  },
};

const theme = createTheme({
  palette: {
    error: {
      main: '#E35D6A', // Coral red for "Ikke siste versjon"
    },
    warning: {
      main: '#F4A261', // Orange/amber for "Erstattet"
    },
    info: {
      main: '#ffffff', // White background for "Merknad"
      contrastText: '#000000', // Black text for "Merknad"
    },
    secondary: {
      main: '#000000', // Black for "Tilbaketrukket"
    },
  },
});

const getStatusConfig = (status: StatusCode) => {
  return statusConfig[status] ?? statusConfig[0]; // Fallback to status 0
};

export const StatusChip = ({
  status,
  lastUpdated,
  published,
  description,
  showLabel = true,
  onRemove,
  onReplace,
  onAdd,
  ...props
}: StatusChipProps) => {
  const [open, setOpen] = useState(false);

  if (status === 1) return null;

  const { icon: Icon, label, color } = getStatusConfig(status as StatusCode);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Chip
        icon={
          <Box sx={{ ml: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon />
          </Box>
        }
        label={showLabel ? label : ''}
        color={color}
        size="small"
        onClick={handleClick}
        sx={{
          ...(props.sx || {}),
          overflow: 'hidden',
          borderRadius: '999px',
          ...(showLabel ? { pl: '2px' } : {}), // add gap from left edge only with label
          '& .MuiChip-icon': {
            p: 0,
            ...(showLabel ? {} : { m: 0 }), // center icon-only; keep MUI margins with label
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiChip-icon svg': {
            width: 16,
            height: 16,
            display: 'block',
          },
          ...(!showLabel && {
            '& .MuiChip-label': { display: 'none' },
            padding: '0',
            width: '24px',
            height: '24px',
          }),
        }}
        {...props}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            py: 0.25,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            bgcolor: (theme) => theme.palette[color].main,
            color: (theme) => theme.palette[color].contrastText,
            minHeight: 32,
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
            },
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Icon sx={{ mr: 1, fontSize: 18 }} />
            {label}
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              color: (theme) => theme.palette[color].contrastText,
              p: 0.25,
            }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2.5, mb: 1 }}>
            <Typography sx={{ display: 'flex', mb: 0.5, fontSize: '1rem' }}>
              <Box component="span" sx={{ width: 100 }}>
                Oppdatert
              </Box>
              {lastUpdated}
            </Typography>
            <Typography sx={{ display: 'flex', fontSize: '1rem' }}>
              <Box component="span" sx={{ width: 100 }}>
                Publisert
              </Box>
              {published}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: '1rem' }}>{description}</Typography>
        </DialogContent>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {onRemove && (
            <Button variant="outlined" onClick={onRemove}>
              Fjern
            </Button>
          )}
          {onReplace && (
            <Button variant="outlined" onClick={onReplace}>
              Erstatt
            </Button>
          )}
          {onAdd && (
            <Button variant="contained" onClick={onAdd}>
              Legg til
            </Button>
          )}
        </Box>
      </Dialog>
    </ThemeProvider>
  );
};
